import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Stack, ChipOwnProps, Chip, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  TaskListFragment,
  TaskStatus,
  CreateTaskDocument,
  CreateTaskMutation,
  CreateTaskMutationVariables,
} from '../../../generated/graphql';
import { useNavigate } from 'react-router';
import { appPaths } from '../../../app-paths';
import { useAlert } from '@dwelvio/react-alert';
import { TaskStatusTypeLabelArray } from '../../../types';
import { NewTaskDialog } from '../../../components/new-task-dialog';
import { useApp } from '../../../components/app-context';
import { AddIcon } from '../../../components/icons';

const colors: Record<TaskStatus, ChipOwnProps['color']> = {
  todo: 'default',
  in_progress: 'info',
  completed: 'success',
};

interface ContractTasksListProps {
  contractId: string;
  tasks: TaskListFragment[];
  onRefresh: () => void;
  disabled?: boolean;
}

export function ContractTasksList({ contractId, tasks, onRefresh, disabled }: ContractTasksListProps) {
  const navigate = useNavigate();
  const app = useApp();
  const { show } = useAlert();
  const [showAddNewTask, setShowAddNewTask] = useState<boolean>(false);

  const [createTask, { loading: createLoading }] = useMutation<CreateTaskMutation, CreateTaskMutationVariables>(
    CreateTaskDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa vytvoriť úlohu.', e.message);
      },
      onCompleted: () => {
        onRefresh();
      },
    },
  );

  const isOverdue = (task: TaskListFragment) => {
    if (task.completedAt || !task.dueDate) return false;
    return new Date(task.dueDate) < new Date();
  };

  const columns = React.useMemo((): GridColDef<TaskListFragment>[] => {
    return [
      {
        field: 'overdue',
        headerName: '',
        width: 50,
        renderCell: (params) => {
          return isOverdue(params.row) ? (
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: 'red',
              }}
            />
          ) : null;
        },
        sortable: false,
      },
      {
        field: 'title',
        headerName: 'Názov úlohy',
        width: 250,
      },
      {
        field: 'status',
        headerName: 'Stav',
        width: 110,
        renderCell: (params) => {
          const status = TaskStatusTypeLabelArray.find((s) => s.value === params.value) ?? {
            label: 'xxx',
            value: 'todo',
          };
          const color = colors[status.value];
          return <Chip label={status.label} color={color} size="small" />;
        },
        sortable: true,
      },
      {
        field: 'dueDate',
        headerName: 'Termín splnenia',
        type: 'string',
        valueGetter: (value: string) => value ?? '',
        valueFormatter: (value: string) => (value ? app.formatDate(value) : ''),
        width: 130,
      },
      {
        field: 'lastComment',
        headerName: 'Posledný komentár',
        valueGetter: (value: string, row) => row.lastComment?.comment ?? '',
        width: 300,
        sortable: true,
      },
    ];
  }, [app]);

  return (
    <Stack direction="column" flexDirection="column" width="100%" sx={{ height: 400 }} spacing={2}>
      <Button
        variant="outlined"
        onClick={() => setShowAddNewTask(true)}
        startIcon={<AddIcon />}
        disabled={disabled}
        sx={{ alignSelf: 'flex-start' }}
      >
        Vytvor úlohu
      </Button>

      <DataGrid
        rows={tasks}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
          sorting: {
            sortModel: [{ field: 'dueDate', sort: 'asc' }],
          },
        }}
        pageSizeOptions={[5, 10, 20]}
        disableRowSelectionOnClick
        onRowClick={(params) => {
          const taskId = params.row.id;
          navigate(appPaths.task.navigate({ taskId }));
        }}
      />

      {!disabled && (
        <>
          {showAddNewTask && (
            <NewTaskDialog
              open={showAddNewTask}
              onClose={() => setShowAddNewTask(false)}
              onSubmit={async (input) => {
                await createTask({
                  variables: {
                    input: {
                      ...input,
                      status: 'todo',
                      contractId: contractId,
                    },
                  },
                });
                setShowAddNewTask(false);
                onRefresh();
              }}
              loading={createLoading}
              contractId={contractId}
            />
          )}
        </>
      )}
    </Stack>
  );
}
